"use client";

import type React from "react";
import { useState, useEffect, useRef, useId } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import styles from "./Modal.module.scss";
import assetLoader from "@utils/internalImageLoder";

// Include the cn utility function directly in this file
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
  // New customization props
  overlayClassName?: string;
  contentClassName?: string;
  headerClassName?: string;
  closeButtonClassName?: string;
  bodyClassName?: string;
  backdropBlur?: boolean;
  zIndex?: string;
  showCloseButton?: boolean;
  closeIcon?: React.ReactNode;
  closeIconSize?: number;
  animation?: {
    overlay?: string;
    content?: string;
  };
  preventScroll?: boolean;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  className,
  // Default values for new props
  overlayClassName,
  contentClassName,
  headerClassName,
  closeButtonClassName,
  bodyClassName,
  backdropBlur = true,
  zIndex = "z-50",
  showCloseButton = true,
  closeIcon,
  closeIconSize = 18,
  animation = {
    overlay: "animate-fadeIn",
    content: "animate-slideDown",
  },
  preventScroll = true,
}: ModalProps) {
  const [isMounted, setIsMounted] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    setIsMounted(true);

    // Prevent body scrolling when modal is open
    if (isOpen && preventScroll) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen, preventScroll]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  const handleEscapeKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  if (!isMounted || !isOpen) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className={cn(
        styles.modalOverlay,
        backdropBlur && styles.backdropBlur,
        animation.overlay === "animate-fadeIn" && styles.animateFadeIn,
        overlayClassName
      )}
      onClick={handleOverlayClick}
      style={{ zIndex: zIndex === "z-50" ? 50 : undefined }}
    >
      <div
        className={cn(
          styles.modalContent,
          animation.content === "animate-slideDown" && styles.animateSlideDown,
          contentClassName,
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
      >
        {(title || showCloseButton) && (
          <div
            className={cn(
              styles.modalHeader,
              headerClassName
            )}
          >
            {title && (
              <h2 id={titleId} className={styles.modalTitle}>{title}</h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className={cn(
                  styles.modalCloseButton,
                  closeButtonClassName
                )}
              >
                {closeIcon || (
                  <Image
                    src={`${assetLoader()}/icons/Close_Dark.svg`}
                    alt="Close"
                    width={closeIconSize}
                    height={closeIconSize}
                  />
                )}
                <span className={styles.srOnly}>Close</span>
              </button>
            )}
          </div>
        )}
        <div className={cn(styles.modalBody, bodyClassName)}>{children}</div>
      </div>
    </div>,
    document.body
  );
}
